/**
 * 3D 처리 진행 단계 — 그날 결과 json 이 아직 없을 때 기록의 LRM 작업 상태(ai_suggestions.lrm)로 '어디까지 왔는지'를 보인다.
 * 서버(lrmJobs)가 LRM 작업 상태를 그대로 옮겨 두므로 화면은 읽기만 한다.
 */
import type { GrowthRecord } from '@grosync/common';

export type LrmPhase = 'none' | 'registration' | 'queued' | 'running' | 'publishing' | 'failed';

interface LrmState {
  status?: string | null;
  stage?: string | null;
  terminal?: boolean;
  publishedAt?: string | null;
  error?: { code?: string; message?: string } | null;
}

const REGISTRATION_CODES = new Set(['not_registered', 'measurement_profile_required']);

function lrmOf(rec: GrowthRecord | null | undefined): LrmState | null {
  return ((rec?.aiSuggestions as { lrm?: LrmState } | null | undefined)?.lrm ?? null) as LrmState | null;
}

export function lrmPhase(rec: GrowthRecord | null | undefined): LrmPhase {
  const l = lrmOf(rec);
  if (!l) return 'none';
  if (l.error?.code && REGISTRATION_CODES.has(l.error.code)) return 'registration';
  if (l.status === 'failed' || (l.terminal && l.error)) return 'failed';
  if (l.status === 'running') return 'running';
  if (l.status === 'queued' || l.status === 'submitting') return 'queued';
  if (l.status === 'succeeded') return 'publishing';         // LRM 은 끝났고 결과를 가져와 반영하는 중
  return 'none';
}

const LABEL: Record<LrmPhase, string> = {
  none: '3D 처리 전',
  registration: '관부 등록 필요',
  queued: '3D 처리 대기 중',
  running: '3D 처리 중',
  publishing: '결과 반영 중',
  failed: '3D 처리 실패',
};

/** 결과가 아직 없을 때 보일 문구 — 실패면 실제 사유를 붙인다 */
export function lrmProgressLabel(rec: GrowthRecord | null | undefined): string {
  const p = lrmPhase(rec);
  if (p === 'failed') {
    const msg = lrmOf(rec)?.error?.message;
    return msg ? `${LABEL.failed} — ${msg}` : LABEL.failed;
  }
  return LABEL[p];
}

/** 짧은 배지용 (사유 없이) */
export function lrmProgressShort(rec: GrowthRecord | null | undefined): string {
  return LABEL[lrmPhase(rec)];
}

/** 화면이 저절로 다시 읽어야 하는 단계 (대기·처리·반영 중) */
export function isLrmInFlight(rec: GrowthRecord | null | undefined): boolean {
  const p = lrmPhase(rec);
  return p === 'queued' || p === 'running' || p === 'publishing';
}
